import { useApi } from '@/api';
import {
  ChartTimeFilter,
  ExpandableKpiCard,
  ModalInfo,
  Page
} from '@/components';
import { useChartFillDatasetsDates, useSparkColor } from '@/hooks';
import {
  ArrowUpRightBoxIcon,
  Box,
  ChartSkeleton,
  CurrencyValue,
  Flex,
  formatNumber,
  Heading,
  KpiCard,
  LineChart
} from '@blockanalitica/ui';
import { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import TokenVaults from './TokenVaults';

type DataResponse = {
  symbol: string;
  circulating_supply: string;
  networks: string[];
  rate: string;
  underlying_address: string;
  underlying_symbol: string;
  address: string;
  historic: SimpleChartData[];
};

export default function SavingsSymbolPage() {
  const color = useSparkColor();
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const { symbol } = useParams();

  const [data, error] = useApi<DataResponse>({
    url: `/sparkstar/savings/vaults/${symbol}/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });
  const datasets = [
    {
      xField: 'date',
      yField: 'circulating_supply',
      borderColor: color
    }
  ];

  const symbolToTextMap = {
    sDAI: (
      <ModalInfo title="What is sUSDS?" iconSize="2">
        <Flex flexDirection="column" gap="2xs">
          <Box>
            Savings Dai (sDAI) is the old version of sUSDS. sDai is a tokenized
            representation of Dai deposited in the Dai Savings Rate (DSR)
            offered by Sky. The sDAI token enables users to receive returns on
            their DSR deposits while still being able to transfer, stake, lend
            and use it in any way they want.
          </Box>
          <Flex
            as="a"
            display="inline-flex"
            // @ts-ignore
            href="https://docs.spark.fi/dev/savings/sdai-token"
            target="_blank"
            rel="noopener noreferrer">
            Learn more
            <ArrowUpRightBoxIcon size="-1" />
          </Flex>
        </Flex>
      </ModalInfo>
    ),
    sUSDC: (
      <ModalInfo title="What is sUSDS?" iconSize="2">
        <Flex flexDirection="column" gap="2xs">
          <Box>
            Savings USDC (sUSDC) is an ERC-4626 representation of USDC in the
            Spark USDC Vault. sUSDC allows users to deposit USDC to receive the
            yield generated by the Sky Savings Rate while still being able to
            transfer, stake, lend the tokens. Converting between USDC and sUSDC
            can be done using the ERC4626 interface.
          </Box>
          <Flex
            as="a"
            display="inline-flex"
            // @ts-ignore
            href="https://docs.spark.fi/dev/savings/susdc-token"
            target="_blank"
            rel="noopener noreferrer">
            Learn more
            <ArrowUpRightBoxIcon size="-1" />
          </Flex>
        </Flex>
      </ModalInfo>
    ),
    sUSDS: (
      <ModalInfo title="What is sUSDS?" iconSize="2">
        <Flex flexDirection="column" gap="2xs">
          <Box>
            avings USDS (sUSDS) is an ERC-4626 representation/wrapper of USDS in
            the Sky Savings Rate module (SSR). sUSDS allows users to deposit
            USDS to receive the yield generated by the Sky Protocol while still
            being able to transfer, stake, lend the tokens. Converting between
            USDS and sUSDS can be done using the ERC4626 interface.
          </Box>
          <Flex
            as="a"
            display="inline-flex"
            // @ts-ignore
            href="https://docs.spark.fi/dev/savings/susds-token"
            target="_blank"
            rel="noopener noreferrer">
            Learn more
            <ArrowUpRightBoxIcon size="-1" />
          </Flex>
        </Flex>
      </ModalInfo>
    )
  };

  const symbolText = symbolToTextMap[symbol as keyof typeof symbolToTextMap];

  const chartData = useChartFillDatasetsDates(
    data?.historic ?? [],
    datasets,
    chartDaysAgo
  );
  const valueFormatter = useCallback((value: string | number) => {
    return (
      <CurrencyValue
        value={value}
        variant="dynamic"
        options={{
          currency: 'USD'
        }}
      />
    );
  }, []);

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }

  return (
    <Page
      title={
        <Flex alignItems="center" gap="xs">
          <Heading>{data['symbol']}</Heading> {symbolText}
        </Flex>
      }>
      <Flex flexDirection="column" gap="s"></Flex>
      <ExpandableKpiCard
        text={`Deposits to ${data['symbol']}`}
        defaultShowChart={true}
        hoverBorderColor={color}
        kpiCard={
          <KpiCard
            data={{
              circulating_supply: data['circulating_supply']
            }}
            field="circulating_supply"
            options={{
              currency: 'USD',
              variants: {
                kpiCardValue: 'mainValue'
              }
            }}
            variant="nestedAsChild"
          />
        }>
        <LineChart
          data={chartData}
          datasets={datasets}
          valueFormatter={valueFormatter}
          showValuesInTooltip={true}
          tooltip={{
            valueFormatter: (value) => {
              return formatNumber(Number(value));
            },
            labelFormatter: () => 'Total',
            displayColors: false
          }}
          timeFilter={
            <ChartTimeFilter
              activeOption={chartDaysAgo}
              onChange={setChartDaysAgo}
            />
          }
        />
      </ExpandableKpiCard>

      <TokenVaults />
    </Page>
  );
}
